import clsx from "clsx";
import React, { useRef, useEffect, useState, forwardRef } from "react";
import { HiArrowUp } from "react-icons/hi";

interface ScrollRestoreProps {
  children: React.ReactNode;
  id: string;
  className?: string;
  initRestore?: boolean;
  debounceTime?: number;
  showScrollTopButton?: boolean;
}

const ScrollRestore = forwardRef<HTMLDivElement, ScrollRestoreProps>(
  (
    {
      children,
      id,
      className,
      initRestore,
      debounceTime = 100,
      showScrollTopButton = false,
    },
    ref
  ) => {
    const scrollableContentRef = useRef<HTMLDivElement | null>(null);
    const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [scrollPosition, setScrollPosition] = useState(0);

    const handleScroll = () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        if (scrollableContentRef.current) {
          sessionStorage.setItem(
            id,
            scrollableContentRef.current.scrollTop.toString()
          );
          setScrollPosition(scrollableContentRef.current.scrollTop);
        }
      }, debounceTime);
    };

    useEffect(() => {
      // if (!initRestore) return;
      // 새로고침 시 sessionStorage에서 해당 id 제거
      const handleBeforeUnload = () => {
        sessionStorage.removeItem(id);
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      const savedScroll = sessionStorage.getItem(id) || "0";
      if (scrollableContentRef.current) {
        scrollableContentRef.current.scrollTop = parseInt(savedScroll, 10);
      }

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }, [id, initRestore]);

    useEffect(() => {
      return () => {
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
      };
    }, []);

    // ref가 전달되면 외부 ref 사용, 없으면 내부 ref 사용
    const mergedRef = (node: HTMLDivElement | null) => {
      scrollableContentRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    return (
      <div
        ref={mergedRef}
        onScroll={handleScroll}
        className={clsx(className, {
          "overflow-y-scroll": !className?.includes("overflow-y-scroll"),
        })}
      >
        {children}
        {/* 우측 하단 버튼 */}
        {/* 노출: 스크롤이 0이상일 경우, 클릭시: 스크롤 최상단으로 이동 */}
        {scrollPosition > 0 && showScrollTopButton && (
          <div
            onClick={() => {
              if (scrollableContentRef.current) {
                scrollableContentRef.current.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }
            }}
            className="z-50 h-12 w-12 opacity-95 transition-opacity duration-300 rounded-full bg-[#1562fc] absolute right-4 bottom-[80px] flex items-center justify-center cursor-pointer"
          >
            <HiArrowUp className="text-white text-2xl" />
          </div>
        )}
      </div>
    );
  }
);

ScrollRestore.displayName = "ScrollRestore";

export default ScrollRestore;
